/**
 * src/components/dashboard/ExpressedInterestModal.tsx
 *
 * File-level:
 * - Modal that lists expressed interest rows for the logged-in user (toUserId).
 * - Visuals match ShortlistModal: gradient header, soft rose modal background,
 *   slightly rounded cards, consistent action buttons, backdrop blur and elevated shadow.
 *
 * Notes:
 * - Preserves existing behavior: Accept/Reject actions, locks buttons after action,
 *   dispatches a window event on success and navigates to profile via hashed links.
 * - Written in TypeScript and includes JSDoc comments for file, component and functions.
 */

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Heart, Check, X, Eye, User } from 'lucide-react';
import { getExpressedInterestList, postInterestAction } from '../../services/interestApi';
import { Button } from '../ui/button';
import { fetchJSON, apiBase } from '@/utils/api';

/**
 * InterestRecord
 * @description Model for a single expressed-interest record shown in the modal
 */
interface InterestRecord {
  /** Numeric id from DB */
  id: number;
  /** Sender user id (who expressed interest) */
  fromUserId: string;
  /** Recipient user id (should match logged-in user) */
  toUserId?: string;
  /** Optional message accompanying the interest */
  message?: string;
  /** Status string: Pending | Accepted | Rejected */
  status?: string;
  /** Timestamp when expressed */
  expressedAt?: string;
  /** User details */
  firstName?: string;
  lastName?: string;
  profession?: string;
  avatarUrl?: string;
}

/**
 * resolveAbsoluteMediaUrl
 * @description Convert relative media paths to absolute using runtime apiBase()
 */
function resolveAbsoluteMediaUrl(pathOrUrl?: string | null) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  try {
    const base = String(apiBase?.() ?? '').replace(/\/$/, '');
    if (!base) return pathOrUrl;
    const p = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${base}${p}`;
  } catch {
    return pathOrUrl;
  }
}

/**
 * Avatar
 * @description Compact avatar with initials fallback
 */
function Avatar({ src, alt, size = 48 }: { src?: string | null; alt?: string; size?: number }) {
  const [imgVisible, setImgVisible] = useState<boolean>(Boolean(src));
  useEffect(() => setImgVisible(Boolean(src)), [src]);
  const initials = (alt || '').split(' ').map(s => s?.[0] ?? '').join('').slice(0, 2).toUpperCase();

  return (
    <div
      className="relative flex-shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {imgVisible && src ? (
        <img
          src={src}
          alt={alt ?? 'avatar'}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
            setImgVisible(false);
          }}
        />
      ) : null}
      {!imgVisible && <span className="text-slate-700 font-semibold">{initials || '—'}</span>}
    </div>
  );
}

/**
 * ExpressedInterestModalProps
 * @description Props for the modal component
 */
interface ExpressedInterestModalProps {
  /** Recipient user id (logged-in user) */
  toUserId?: string;
  /** Backwards-compatible alias */
  currentUserId?: string;
  /** Close handler */
  onClose: () => void;
}

/**
 * ExpressedInterestModal
 *
 * @description Modal that lists incoming expressed interest rows for the recipient.
 * Visual style intentionally mirrors ShortlistModal:
 * - Modal body: bg-rose-50, rounded-lg, shadow-lg
 * - Header: colorful gradient (indigo -> purple -> pink) with compact stats pill
 * - Cards: white background, soft border and rounded corners
 *
 * @param props ExpressedInterestModalProps
 * @returns JSX.Element
 */
export default function ExpressedInterestModal({
  toUserId,
  currentUserId,
  onClose,
}: ExpressedInterestModalProps): JSX.Element {
  const recipient = toUserId ?? currentUserId ?? '';

  const [items, setItems] = useState<InterestRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [processingIds, setProcessingIds] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;

  // Caches for avatar and user details
  const avatarCacheRef = useRef<Map<string, string | null>>(new Map());
  const userDetailsCacheRef = useRef<Map<string, { firstName?: string; lastName?: string; profession?: string }>>(new Map());
  const inflightAvatarsRef = useRef<Record<string, boolean>>({});
  const inflightDetailsRef = useRef<Record<string, boolean>>({});

  /**
   * fetchAvatarForProfile
   * @description Resolve avatar URL from multiple endpoints
   */
  async function fetchAvatarForProfile(pid: string) {
    if (!pid) return null;
    if (avatarCacheRef.current.has(pid)) return avatarCacheRef.current.get(pid) ?? null;
    if (inflightAvatarsRef.current[pid]) return null;

    inflightAvatarsRef.current[pid] = true;
    try {
      // Try dashboard/user-photos
      try {
        const j = await fetchJSON(`/dashboard/user-photos/${encodeURIComponent(pid)}`);
        if (j && j.success && Array.isArray(j.photos) && j.photos.length > 0) {
          const p = j.photos[0];
          const cand = p.src || p.photoUrl || p.url || p.filePath || p.fileName;
          if (cand) {
            const resolved = resolveAbsoluteMediaUrl(cand);
            avatarCacheRef.current.set(pid, resolved);
            return resolved;
          }
        }
      } catch {
        // ignore
      }

      // Try folder listing
      try {
        const j = await fetchJSON(`/user-files/${encodeURIComponent(pid)}/Photos`);
        if (j && j.success && Array.isArray(j.files) && j.files.length > 0) {
          const f = j.files[0];
          const fileName = f.fileName || f.name;
          if (fileName) {
            const host = String(apiBase?.() ?? '').replace(/\/$/, '').replace(/\/api$/, '');
            const resolved = `${host}/Media/${pid}/Photos/${fileName}`;
            avatarCacheRef.current.set(pid, resolved);
            return resolved;
          }
        }
      } catch {
        // ignore
      }

      // Try users endpoint
      try {
        const ures = await fetchJSON(`/users/${encodeURIComponent(pid)}`);
        const u = ures?.data ?? ures;
        const candidates = [u?.avatarUrl, u?.photoUrl, u?.profilePhoto, u?.photo, u?.image];
        for (const cand of candidates) {
          if (cand) {
            const resolved = resolveAbsoluteMediaUrl(cand);
            avatarCacheRef.current.set(pid, resolved);
            return resolved;
          }
        }
      } catch {
        // ignore
      }

      avatarCacheRef.current.set(pid, null);
      return null;
    } finally {
      delete inflightAvatarsRef.current[pid];
    }
  }

  /**
   * fetchUserDetails
   * @description Fetch firstName, lastName, profession for a user
   */
  async function fetchUserDetails(pid: string) {
    console.log('[ExpressedInterestModal] fetchUserDetails called for:', pid);
    if (!pid) {
      console.log('[ExpressedInterestModal] No pid provided');
      return null;
    }
    if (userDetailsCacheRef.current.has(pid)) {
      console.log('[ExpressedInterestModal] Returning cached details for:', pid);
      return userDetailsCacheRef.current.get(pid) ?? null;
    }
    if (inflightDetailsRef.current[pid]) {
      console.log('[ExpressedInterestModal] Already fetching details for:', pid);
      return null;
    }

    inflightDetailsRef.current[pid] = true;
    try {
      console.log('[ExpressedInterestModal] Calling API /users/' + pid);
      const res = await fetchJSON(`/users/${encodeURIComponent(pid)}`);
      console.log('[ExpressedInterestModal] API response for', pid, ':', res);
      
      // API returns { success: true, user: {...} }
      const u = res?.user ?? res?.data ?? res;
      console.log('[ExpressedInterestModal] User data:', u);
      
      if (u) {
        const details = {
          firstName: u.firstName ?? u.first_name ?? '',
          lastName: u.lastName ?? u.last_name ?? '',
          profession: u.profession ?? u.job ?? u.occupation ?? ''
        };
        console.log('[ExpressedInterestModal] Extracted details from /users:', details);
        
        // If profession is missing, try /profiles endpoint
        if (!details.profession || details.profession.trim() === '') {
          console.log('[ExpressedInterestModal] No profession in /users, trying /profiles fallback');
          try {
            const pres = await fetchJSON(`/profiles/user/${encodeURIComponent(pid)}?minimal=1`);
            console.log('[ExpressedInterestModal] /profiles response:', pres);
            const p = pres?.profile ?? pres?.data ?? pres;
            console.log('[ExpressedInterestModal] Extracted profile:', p);
            
            const profession = p?.basicDetails?.profession ?? p?.basic?.profession ?? p?.personalDetails?.occupation ?? p?.personal?.occupation ?? '';
            if (profession && profession.trim() !== '') {
              details.profession = profession;
              console.log('[ExpressedInterestModal] Found profession in profiles:', profession);
            }
          } catch (err) {
            console.log('[ExpressedInterestModal] /profiles call failed:', err);
          }
        }
        
        userDetailsCacheRef.current.set(pid, details);
        return details;
      }

      userDetailsCacheRef.current.set(pid, {});
      return {};
    } catch (error) {
      console.error('[ExpressedInterestModal] Error fetching user details:', error);
      userDetailsCacheRef.current.set(pid, {});
      return {};
    } finally {
      delete inflightDetailsRef.current[pid];
    }
  }

  /**
   * load
   * @description Fetch incoming interest records for the recipient and normalize shape.
   */
  async function load(): Promise<void> {
    if (!recipient) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getExpressedInterestList(recipient, { status: 'Pending', limit: 200 });
      const list: any[] = Array.isArray(res?.list) ? res.list : [];

      // Normalize common shapes to InterestRecord
      const normalized: InterestRecord[] = (list || [])
        .filter((r: any) => {
          if (!r) return false;
          const to = String(r.toUserId ?? r.to_user_id ?? r.to ?? r.to_user ?? '');
          return String(to) === String(recipient);
        })
        .map((r: any) => ({
          id: Number(r.id ?? r._id ?? r.interest_id ?? 0),
          fromUserId:
            String(
              r.fromUserId ??
                r.from_user_id ??
                r.from ??
                r.from_user ??
                r.sender ??
                r.requester ??
                r.userId ??
                r.user_id ??
                '—'
            ),
          toUserId: String(r.toUserId ?? r.to_user_id ?? r.to ?? r.to_user ?? ''),
          message: r.message ?? r.note ?? r.msg ?? '',
          status: String(r.status ?? r.state ?? r.current_status ?? 'Pending'),
          expressedAt: r.expressedAt ?? r.expressed_at ?? r.createdAt ?? r.created_at ?? r.timestamp ?? undefined,
        }));

      setItems(normalized);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ExpressedInterestModal] load failed', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipient]);

  /**
   * Load user details (avatar, name, profession) for all items
   */
  useEffect(() => {
    if (items.length === 0) {
      console.log('[ExpressedInterestModal] No items to fetch details for');
      return;
    }
    
    const uniqueUserIds = Array.from(new Set(items.map(item => item.fromUserId)));
    console.log('[ExpressedInterestModal] Items loaded, fetching details for users:', uniqueUserIds);
    console.log('[ExpressedInterestModal] Current items state:', items);
    
    // Fetch details for all users
    const fetchAllDetails = async () => {
      for (const userId of uniqueUserIds) {
        // Skip if already has complete details
        const item = items.find(i => i.fromUserId === userId);
        if (item?.firstName && item?.lastName && item?.profession && item?.avatarUrl) {
          console.log('[ExpressedInterestModal] Skipping', userId, '- already has details');
          continue;
        }

        console.log('[ExpressedInterestModal] Fetching details for:', userId);
        
        try {
          const [avatar, details] = await Promise.all([
            fetchAvatarForProfile(userId),
            fetchUserDetails(userId)
          ]);
          
          console.log('[ExpressedInterestModal] Fetched for', userId, ':', { avatar, details });
          
          setItems(prev => {
            const updated = prev.map(item => {
              if (item.fromUserId === userId) {
                const updatedItem = {
                  ...item,
                  avatarUrl: avatar || item.avatarUrl,
                  firstName: details?.firstName || item.firstName,
                  lastName: details?.lastName || item.lastName,
                  profession: details?.profession || item.profession
                };
                console.log('[ExpressedInterestModal] Updated item:', updatedItem);
                return updatedItem;
              }
              return item;
            });
            console.log('[ExpressedInterestModal] All items after update:', updated);
            return updated;
          });
        } catch (error) {
          console.error('[ExpressedInterestModal] Error fetching details for', userId, ':', error);
        }
      }
    };

    fetchAllDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  /**
   * handleAction
   * @description Perform Accept/Reject action for given interest id. Locks buttons after success.
   * @param id number - interest record id
   * @param action 'Accept'|'Reject'
   */
  async function handleAction(id: number, action: 'Accept' | 'Reject'): Promise<void> {
    setProcessingIds((p) => ({ ...p, [String(id)]: true }));
    try {
      await postInterestAction(id, action, recipient || '');
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: action === 'Accept' ? 'Accepted' : 'Rejected' } : it)));

      try {
        window.dispatchEvent(new CustomEvent('expressed-interest-changed', { detail: { delta: -1 } }));
      } catch (evErr) {
        // eslint-disable-next-line no-console
        console.warn('Failed to dispatch expressed-interest-changed event', evErr);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ExpressedInterestModal] action failed', err);
      alert('Action failed: ' + ((err as any)?.message ?? err));
      setProcessingIds((prev) => {
        const c = { ...prev };
        delete c[String(id)];
        return c;
      });
      return;
    }
    // keep locked on success
    setProcessingIds((p) => ({ ...p, [String(id)]: true }));
  }

  // Modal sizing similar to ShortlistModal
  const modalMaxWidth = 'max-w-3xl';
  const modalMaxHeight = 'max-h-[640px]';

  // Count for header pill
  const total = items.length;

  if (typeof document === 'undefined') {
    // Server-side safety: render nothing during SSR
    return <></>;
  }

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-center justify-center px-4 py-6 pointer-events-none">
      <div className="fixed inset-0 bg-black/35 backdrop-blur-sm" onClick={onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Expressed interest"
        className={`relative z-10 w-full ${modalMaxWidth} ${modalMaxHeight} bg-rose-50 rounded-lg shadow-lg overflow-hidden ring-1 ring-rose-100 pointer-events-auto`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Expressed Interest</h3>
              <p className="text-xs opacity-90">Incoming requests</p>
            </div>
            <div className="ml-3 text-xs">
              <div className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full font-medium">{total} Pending</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-transparent text-white border-white/20" onClick={() => load()}>
              Refresh
            </Button>

            <button aria-label="Close" onClick={onClose} className="p-2 rounded hover:bg-white/10 text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto" style={{ height: 'calc(100% - 112px)' }}>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white border rounded-lg p-3 h-24" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-3">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <h4 className="text-sm font-semibold text-slate-800 mb-1">No expressed interest</h4>
              <p className="text-xs text-slate-500 mb-3">You have no pending requests at the moment.</p>
              <div className="flex justify-center gap-2">
                <Button size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" onClick={onClose}>
                  Continue
                </Button>
                <Button variant="outline" className="bg-transparent border-slate-200 text-slate-700" onClick={() => (window.location.hash = '#/search')}>
                  Find Matches
                </Button>
              </div>
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                {items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((item) => {
                  const locked = Boolean(item.status && item.status !== 'Pending') || Boolean(processingIds[String(item.id)]);
                  const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
                  
                  // Debug logging
                  console.log('[ExpressedInterestModal] Rendering item:', {
                    fromUserId: item.fromUserId,
                    firstName: item.firstName,
                    lastName: item.lastName,
                    fullName: fullName,
                    profession: item.profession,
                    avatarUrl: item.avatarUrl
                  });
                  
                  return (
                    <li key={item.id} className="bg-white border-2 border-rose-200 rounded-lg p-3 flex items-center justify-between gap-3 hover:shadow-md hover:border-rose-300 transition">
                      <div className="flex items-start gap-3 min-w-0">
                        <Avatar 
                          src={item.avatarUrl} 
                          alt={fullName || item.fromUserId}
                          size={48}
                        />

                        <div className="min-w-0 flex-1">
                          {fullName ? (
                            <>
                              <div className="text-sm font-semibold truncate">{fullName}</div>
                              <div className="text-xs text-slate-500 mt-0.5 truncate">{item.fromUserId}</div>
                            </>
                          ) : (
                            <div className="text-sm font-semibold truncate">{item.fromUserId}</div>
                          )}
                          <div className="text-xs text-slate-500 mt-1 truncate">{item.profession || 'Profession not available'}</div>
                          {item.message && <div className="text-xs text-slate-600 mt-1 truncate italic">{item.message}</div>}
                          {item.expressedAt && <div className="text-xs text-slate-400 mt-1">{new Date(item.expressedAt).toLocaleDateString()}</div>}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block px-2 py-1 text-xs rounded ${
                            item.status === 'Accepted' ? 'bg-green-100 text-green-700' : item.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {item.status ?? 'Pending'}
                        </span>

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAction(item.id, 'Accept')}
                            disabled={locked}
                            className={`bg-green-600 text-white disabled:opacity-50 ${locked ? 'cursor-not-allowed' : ''}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Accept
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => handleAction(item.id, 'Reject')}
                            disabled={locked}
                            className={`bg-red-600 text-white disabled:opacity-50 ${locked ? 'cursor-not-allowed' : ''}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>

                          <Button
                            size="sm"
                            onClick={() => (window.location.hash = `#/search-profile/${encodeURIComponent(item.fromUserId)}`)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              
              {/* Pagination Controls */}
              {items.length > itemsPerPage && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-rose-200">
                  <div className="text-xs text-slate-600">
                    Showing {Math.min((currentPage - 1) * itemsPerPage + 1, items.length)} - {Math.min(currentPage * itemsPerPage, items.length)} of {items.length}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="disabled:opacity-50"
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.ceil(items.length / itemsPerPage) }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          size="sm"
                          variant={currentPage === page ? "default" : "outline"}
                          onClick={() => setCurrentPage(page)}
                          className={currentPage === page ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white" : ""}
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(items.length / itemsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(items.length / itemsPerPage)}
                      className="disabled:opacity-50"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end px-3 py-2 border-t">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}